import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TransferCommonParameters } from '@app/client/uploader/interfaces/transfer';
import { PrepareActions } from '@app/client/uploader/store/actions';
import * as fromRoot from '@app/reducers';
import { Customization } from '@app/shared/models/customization';
import { TransferSettings } from '@app/shared/models/domainSettings';
import { Promotion } from '@app/shared/models/promotion';
import { DialogService } from '@ngneat/dialog';
import { select, Store } from '@ngrx/store';
import { GetRecordOutput } from '@smash-sdk/domain/ejs/01-2024';
import { Observable, Subscription } from 'rxjs';
import { Constant } from 'src/constant';
import * as fromUploader from '@app/client/uploader/store/reducers';
import { ModalCustomizationReceiverComponent } from '../../modals/modal-customization-receiver/modal-customization-receiver.component';
import { ModalPromotionComponent } from '../../modals/modal-promotion/modal-promotion.component';
@Component({
  selector: 'app-config-send-parameters',
  templateUrl: './config-send-parameters.component.html',
  styleUrls: ['./config-send-parameters.component.scss']
})
export class ConfigSendParametersComponent implements OnInit, OnDestroy {

  @Input() uploadFormMode: string;
  @Input() drawerExpanded: boolean;
  @Input() promotions: Promotion[];
  @Input() promotionSelected: Promotion;
  @Input() promotionLoading: boolean;
  @Input() customizationSelected: Customization;
  @Input() customizationLoading: boolean;
  @Input() transferSettings: TransferSettings;
  @Input() transferCommonParameters: TransferCommonParameters;

  isPremium$: Observable<boolean>;
  transferPassword$: Observable<string>;
  domain$: Observable<GetRecordOutput['record']>;

  parametersForm: FormGroup;

  subscriptions: Subscription[] = [];

  translations = {
    Full: 'UPLOADER_CONFIG_SMASH_PREVIEW_NOTIFICATION_ENABLED',
    All: 'UPLOADER_CONFIG_SMASH_PREVIEW_NOTIFICATION_ENABLED',
    None: 'UPLOADER_CONFIG_SMASH_PREVIEW_NOTIFICATION_DISABLED'
  };

  constructor(private readonly fb: FormBuilder, private store$: Store<fromRoot.State & fromUploader.State>, private dialog: DialogService) {
    this.isPremium$ = this.store$.pipe(select(fromRoot.getIsPremium));
    this.domain$ = this.store$.pipe(select(fromRoot.getCurrentDomain));
    this.transferPassword$ = this.store$.pipe(select(fromUploader.getTransferPassword));
  }

  ngOnInit() {
    this.buildParametersForm();
    this.initUploaderParams();
    this.listenToFormChanges();
    this.subscribeToPasswordChanges();
  }

  subscribeToPasswordChanges() {
    this.subscriptions.push(
      this.transferPassword$.subscribe(password => {
        if (!!password) {
          this.parametersForm.controls.password?.setValue(password, { emitEvent: false });
        }
      })
    );
  }

  buildParametersForm(): void {
    const { password, availabilityDuration, preview, notification } = this.transferCommonParameters;
    // TO FIX: transfer.availabilityDuration unit in days VS dropbox.transfer.availibilityDuraion in seconds
    const isSeconds = this.transferSettings.availabilityDuration.default.toString().length > 3
    const availabilityDurationInDays = {
      default: isSeconds ? this.transferSettings.availabilityDuration.default / Constant.oneDayInSecond : this.transferSettings.availabilityDuration.default,
      min: isSeconds ? this.transferSettings.availabilityDuration.min / Constant.oneDayInSecond : this.transferSettings.availabilityDuration.min,
      max: isSeconds ? this.transferSettings.availabilityDuration.max / Constant.oneDayInSecond : this.transferSettings.availabilityDuration.max,
    }
    // END TO FIX
    this.parametersForm = this.fb.group({
      password: [password ? password : '', Validators.minLength(this.transferSettings.password.minLength)],
      availabilityDuration: [
        availabilityDuration > 0 ? availabilityDuration / Constant.oneDayInSecond : availabilityDurationInDays.default,
        [
          Validators.min(availabilityDurationInDays.min),
          Validators.max(availabilityDurationInDays.max),
          Validators.required,
        ]
      ],
      preview: [
        preview ? preview === 'Full' : this.transferSettings.preview.default === Constant.previewStatus.Full ? true : false,
      ],
      senderNotification: [this.transferSettings.notification?.sender?.enabled],
      receiverNotification: [this.transferSettings.notification?.receiver?.enabled],
      linkNotification: [this.transferSettings.notification?.link?.enabled],
      downloadNotification: [this.transferSettings.notification?.download?.enabled],
      noDownloadNotification: [this.transferSettings.notification?.noDownload?.enabled],
    });
  }

  get f() { return this.parametersForm.controls; }

  initUploaderParams(): void {
    const { password
      , availabilityDuration
      , preview
      , senderNotification
      , receiverNotification
      , linkNotification
      , downloadNotification
      , noDownloadNotification } = this.parametersForm.value;
    this.store$.dispatch(PrepareActions.SetTransferPassword({ password }));
    this.store$.dispatch(PrepareActions.SetTransferAvailability({ availability: availabilityDuration }));
    this.store$.dispatch(PrepareActions.SetTransferPreviewType({ preview: preview ? 'Full' : 'None' }));
    this.store$.dispatch(PrepareActions.SetTransferNotification({
      notification: {
        sender: { enabled: senderNotification },
        receiver: { enabled: receiverNotification },
        link: { enabled: linkNotification },
        download: { enabled: downloadNotification },
        noDownload: { enabled: noDownloadNotification },
      }
    }));
  }

  listenToFormChanges(): void {
    this.parametersForm.valueChanges.subscribe(parametersFormValues => {
      const { password, availabilityDuration, preview, senderNotification, receiverNotification, linkNotification, downloadNotification, noDownloadNotification } = parametersFormValues;
      this.store$.dispatch(PrepareActions.SetTransferPassword({ password }));
      this.store$.dispatch(PrepareActions.SetTransferAvailability({ availability: availabilityDuration }));
      this.store$.dispatch(PrepareActions.SetTransferPreviewType({ preview: preview ? 'Full' : 'None' }));
      this.store$.dispatch(PrepareActions.SetTransferNotification({
        notification: {
          sender: { enabled: senderNotification },
          receiver: { enabled: receiverNotification },
          link: { enabled: linkNotification },
          download: { enabled: downloadNotification },
          noDownload: { enabled: noDownloadNotification },
        }
      }));
    });

    this.parametersForm.statusChanges.subscribe(configFormStatus => {
      this.store$.dispatch(PrepareActions.setConfigFormStatus({ configFormStatus }));
    });
  }

  openModalCustomization() {
    if (!!this.customizationSelected) {
      this.dialog.open(ModalCustomizationReceiverComponent, {
        closeButton: false,
        enableClose: false,
        height: '80vh',
        width: '80vw'
      });
    }
  }

  openModalPromotion() {
    if (!!this.promotionSelected) {
      this.dialog.open(ModalPromotionComponent, {
        closeButton: false,
        enableClose: false,
        height: '80vh',
        width: '80vw'
      });
    }
  }

  get isUpdateCustomizationActive(): boolean {
    return this.transferSettings?.customization?.active;
  }

  get isUpdatePromotionActive(): boolean {
    return this.transferSettings?.promotion?.active;
  }

  get isUpdateAvailabilityDurationActive(): boolean {
    return this.transferSettings?.availabilityDuration?.active;
  }

  get isUpdatePasswordActive(): boolean {
    return this.transferSettings?.password?.active;
  }

  get isUpdatePreviewActive(): boolean {
    return this.transferSettings?.preview?.active;
  }

  get isDisplaySenderNotificationActive(): boolean {
    return this.transferSettings?.notification?.sender?.active;
  }

  get isDisplayLinkNotificationActive(): boolean {
    return this.transferSettings?.notification?.link?.active;
  }

  get isDisplayDownloadNotificationActive(): boolean {
    return this.transferSettings?.notification?.download?.active;
  }

  get isDisplayNoDownloadNotificationActive(): boolean {
    return this.transferSettings?.notification?.noDownload?.active;
  }

  get previewOptions(): any {
    return this.transferSettings?.preview?.options;
  }

  ngOnDestroy() {
    this.subscriptions.map(subscription => subscription.unsubscribe());
    this.subscriptions.length = 0;
  }

}
